import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, Input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BOWKER_BASE_URL, CHILI_FRESH_BASE_URL, CHILI_FRESH_IMAGE_SIZE} from '@aviary/injection-tokens/aviary-only.tokens';
import {COVER_IMAGE_SOURCE, FALLBACK_COVER_IMAGE_URL} from '@aviary/injection-tokens/app-provided.tokens';
import {hasValue, isPopulatedString} from '@aviary/store/common/typing.helpers';
import {NgOptimizedImage} from '@angular/common';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {CoverImageSource, PRIMARY_COVER_IMAGE_SOURCE} from '@aviary/store/common/common.types';
import {deduplicate} from '@aviary/store/transformation.helpers';
import {distinctUntilChanged} from 'rxjs';

export interface MinimalCatalogRecord {
  readonly imageUrl?: string;
  readonly identifier?: string;
}

const X_ALIGNMENTS = ['LEFT', 'CENTER', 'RIGHT'] as const;
export type XAlignment = typeof X_ALIGNMENTS[number];

const Y_ALIGNMENTS = ['TOP', 'CENTER', 'BOTTOM'] as const;
export type YAlignment = typeof Y_ALIGNMENTS[number];

export const DEFAULT_IMAGE_ALT: string = 'cover art';

@Component({
  selector: 'mx-cover-image',
  template: `
    <div class="relative overflow-hidden w-[var(--width)] h-[var(--height)] min-h-[var(--min-height)] max-h-[var(--max-height)]">
      <!-- Some @for trickiness... Create an array of the one number, which changes on every load attempt.  The "track" forces a reload of @for's content. -->
      @for (attemptNumber of [currentLoadAttemptNumber]; track attemptNumber) {
        @if (currentImageUrl) {
          <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -- "object-position" style is much simpler than Tailwind classes here. -->
          <img [ngSrc]="currentImageUrl" fill mxHiddenUntilLoaded [reveal]="loaded" [alt]="alt" [priority]="priority || null" (error)="onError()"
               (load)="onLoad($event)" decoding="async" class="object-contain box-border px-[var(--x-padding)] py-[var(--y-padding)]"
               [style.object-position]="objectPosition"/>
        }
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage, HiddenUntilLoadedDirective]
})
export class CoverImageComponent {

  @Input({required: true})
  public set catalogRecord(catalogRecord: MinimalCatalogRecord | undefined) {
    const newImageUrl: string | undefined = catalogRecord?.imageUrl ?? undefined;
    const newIdentifier: string | undefined = catalogRecord?.identifier ?? undefined;

    if (this.initialized && this.imageUrl === newImageUrl && this.identifier === newIdentifier) {
      /*
        We're already initialized, but the properties that determine the image to load haven't changed.  So don't change anything.
        This prevents an image "flash" when it unloads/reloads, and it also prevents an unnecessary server hit.
      */
      return;
    }

    this.imageUrl = newImageUrl;
    this.identifier = newIdentifier;
    this.initialized ||= true;

    this.rebuildUrls();
  }

  @Input({required: true}) public widthRem: number;
  @Input() public heightRem?: number;
  @Input() public minHeightRem?: number;
  @Input() public maxHeightRem?: number;
  @Input() public xPaddingRem: number = 0;
  @Input() public yPaddingRem: number = 0;

  @Input() public set xAlignment(xAlignment: XAlignment) {
    this.xPosition = X_ALIGNMENTS.includes(xAlignment) ? xAlignment.toLowerCase() : 'center';
    this.objectPosition = `${this.xPosition} ${this.yPosition}`;
  }

  @Input() public set yAlignment(yAlignment: YAlignment) {
    this.yPosition = Y_ALIGNMENTS.includes(yAlignment) ? yAlignment.toLowerCase() : 'center';
    this.objectPosition = `${this.xPosition} ${this.yPosition}`;
  }

  @Input() public alt: string = DEFAULT_IMAGE_ALT;
  @Input() public priority: boolean = false;

  // noinspection CssUnknownProperty
  @HostBinding('style.--width') public get width(): string {
    return `${this.widthRem}rem`;
  }

  // noinspection CssUnknownProperty
  @HostBinding('style.--height') public get height(): string | null {
    return hasValue(this.heightRem) ? `${this.heightRem}rem` : '100%';
  }

  // noinspection CssUnknownProperty
  @HostBinding('style.--min-height') public get minHeight(): string | null {
    return hasValue(this.minHeightRem) ? `${this.minHeightRem}rem` : null;
  }

  // noinspection CssUnknownProperty
  @HostBinding('style.--max-height') public get maxHeight(): string | null {
    return hasValue(this.maxHeightRem) ? `${this.maxHeightRem}rem` : null;
  }

  // noinspection CssUnknownProperty
  @HostBinding('style.--x-padding') public get xPadding(): string {
    return `${this.xPaddingRem}rem`;
  }

  // noinspection CssUnknownProperty
  @HostBinding('style.--y-padding') public get yPadding(): string {
    return `${this.yPaddingRem}rem`;
  }

  public currentImageUrl?: string;
  public currentLoadAttemptNumber: number = 0;
  public loaded: boolean = false;
  public objectPosition: string = 'center center';

  private readonly chiliFreshBaseUrl: string = inject(CHILI_FRESH_BASE_URL);
  private readonly bowkerBaseUrl: string = inject(BOWKER_BASE_URL);
  private readonly chiliFreshImageSize: string = inject(CHILI_FRESH_IMAGE_SIZE);
  private readonly fallbackCoverImageUrl: string = inject(FALLBACK_COVER_IMAGE_URL);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  private initialized: boolean = false;
  private imageUrl?: string;
  private identifier?: string;

  private preferredCoverImageSource: CoverImageSource = PRIMARY_COVER_IMAGE_SOURCE;
  private urlsToTry: string[] = [];
  private currentUrlIndex: number = 0;

  private xPosition: string = 'center';
  private yPosition: string = 'center';

  constructor() {
    inject(COVER_IMAGE_SOURCE)
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(coverImageSource => {
        this.preferredCoverImageSource = coverImageSource;

        this.rebuildUrls();
      });
  }

  public onError(): void {
    this.tryNextUrl();
  }

  public onLoad(event: Event): void {
    const imageElement: HTMLImageElement = event.target as HTMLImageElement;

    if (!imageElement.naturalWidth || !imageElement.naturalHeight || (imageElement.naturalWidth === 1 && imageElement.naturalHeight === 1)) {
      this.tryNextUrl();
      return;
    }

    this.loaded = true;
    this.changeDetector.markForCheck();
  }

  private rebuildUrls(): void {
    this.urlsToTry = deduplicate([this.preferredUrl, this.imageUrl, this.fallbackCoverImageUrl].filter(isPopulatedString));

    const firstUrlToTry: string | undefined = this.urlsToTry[0];

    if (this.currentImageUrl === firstUrlToTry) {
      const matchingIndex: number = this.currentImageUrl ? this.urlsToTry.indexOf(this.currentImageUrl) : -1;
      this.currentUrlIndex = Math.max(matchingIndex, 0); // Just in case the URLs array shrunk or otherwise changed.
    } else {
      this.currentImageUrl = firstUrlToTry; // Might set the url, might clear it.  (The latter removes the <img> from the DOM, due to the @if() wrapper.)
      this.loaded = false;
      this.currentUrlIndex = 0;
      this.currentLoadAttemptNumber += 1;
    }

    this.changeDetector.markForCheck();
  }

  private get preferredUrl(): string | undefined {
    return this.preferredCoverImageSource === 'CHILIFRESH' ? this.chiliFreshUrl : this.bowkerUrl;
  }

  private get chiliFreshUrl(): string | undefined {
    if (!isPopulatedString(this.identifier)) return undefined;

    const url: URL = new URL('/', this.chiliFreshBaseUrl);
    url.searchParams.set('size', this.chiliFreshImageSize);
    url.searchParams.set('isbn', this.identifier);
    return url.toString();
  }

  private get bowkerUrl(): string | undefined {
    if (!isPopulatedString(this.identifier)) return undefined;

    return new URL(this.identifier, this.bowkerBaseUrl).toString();
  }

  private tryNextUrl(): void {
    if (this.currentUrlIndex >= this.urlsToTry.length - 1) return;  // We're stuck with the fallback, and apparently it didn't load for some reason (?).

    this.currentUrlIndex += 1;
    this.currentImageUrl = this.urlsToTry[this.currentUrlIndex];
    this.loaded = false;
    this.currentLoadAttemptNumber += 1;
    this.changeDetector.markForCheck();
  }
}
